import React, { useEffect, useState, useRef, Component } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence, useScroll } from 'framer-motion';
import { Star, ShoppingCart, Heart, Check, Truck, ShieldCheck, Share2, Scale, Utensils, Clock, Award, Leaf, Thermometer, MapPin, FlaskConical, Package, Calendar, HelpCircle, PlayCircle, Zap, CheckCircle2, Info } from 'lucide-react';
// Data & Context
import { products } from '../data/products';
import { productEnrichment, productFAQs } from '../data/enrichedProducts';
import { useCart } from '../context/CartContext';
import { useWishlist } from '../context/WishlistContext';
// UI Components
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Tabs } from '../components/ui/Tabs';
import { Card } from '../components/ui/Card';
import { PageTransition } from '../components/common/PageTransition';
import { Breadcrumb } from '../components/ui/Breadcrumb';
import { ImageZoom } from '../components/ui/ImageZoom';
import { ProgressBar } from '../components/ui/ProgressBar';
import { StepIndicator } from '../components/ui/StepIndicator';
import { BarChart, PieChart } from '../components/ui/DataVisualization';
import { ComparisonTable } from '../components/ui/ComparisonTable';
import { VideoPlayer } from '../components/ui/VideoPlayer';
// Feature Components
import { ProductReviews } from '../components/products/ProductReviews';
import { RelatedProducts } from '../components/products/RelatedProducts';
import { FeedingCalculator } from '../components/tools/FeedingCalculator';
export function ProductDetailPage() {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const navigate = useNavigate();
  const product = products.find(p => p.id === id);
  const enrichedData = id ? productEnrichment[id as keyof typeof productEnrichment] : null;
  const faqs = id ? productFAQs[id as keyof typeof productFAQs] : null;
  const {
    addToCart
  } = useCart();
  const {
    addToWishlist,
    removeFromWishlist,
    isInWishlist
  } = useWishlist();
  // State
  const [quantity, setQuantity] = useState(1);
  const [selectedWeight, setSelectedWeight] = useState('5kg');
  const [subscriptionMode, setSubscriptionMode] = useState<'onetime' | 'subscribe'>('onetime');
  const [deliveryFreq, setDeliveryFreq] = useState('4');
  const [showStickyBar, setShowStickyBar] = useState(false);
  // Refs for scroll detection
  const ctaRef = useRef<HTMLDivElement>(null);
  const {
    scrollY
  } = useScroll();
  useEffect(() => {
    const handleScroll = () => {
      if (ctaRef.current) {
        const ctaBottom = ctaRef.current.getBoundingClientRect().bottom;
        setShowStickyBar(ctaBottom < 0);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  if (!product) {
    return <div className="min-h-screen flex items-center justify-center bg-[#FAF9F6]" data-id="element-6419">
        <div className="text-center" data-id="element-6420">
          <h2 className="text-2xl font-bold mb-4 text-charcoal-900" data-id="element-6421">
            Producto no encontrado
          </h2>
          <Link to="/productos" data-id="element-6422">
            <Button data-id="element-6423">Volver a la tienda</Button>
          </Link>
        </div>
      </div>;
  }
  const isWishlisted = isInWishlist(product.id);
  const breadcrumbItems = [{
    label: 'Inicio',
    href: '/'
  }, {
    label: 'Productos',
    href: '/productos'
  }, {
    label: product.category === 'dog-food' ? 'Perros' : product.category === 'cat-food' ? 'Gatos' : 'Mascotas',
    href: `/productos?category=${product.category}`
  }, {
    label: product.name,
    href: `#`,
    active: true
  }];
  const galleryImages = [product.image, 'https://images.unsplash.com/photo-1589924691195-41432c84c161?auto=format&fit=crop&q=80&w=800', 'https://images.unsplash.com/photo-1623366302587-b38b1ddaefd9?auto=format&fit=crop&q=80&w=800'];
  // --- TABS CONTENT ---
  const tabs = [{
    id: 'description',
    label: 'Descripción',
    content: <div className="space-y-12 animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-6424">
          <div className="prose prose-lg text-charcoal-600 max-w-none" data-id="element-6425">
            <p className="lead text-xl font-light mb-8 leading-relaxed" data-id="element-6426">
              {product.description}
            </p>

            {/* Benefits Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12" data-id="element-6427">
              <div className="bg-primary-50/50 p-8 rounded-3xl border border-primary-100" data-id="element-6428">
                <h3 className="font-serif font-bold text-2xl text-primary-900 mb-6 flex items-center gap-2" data-id="element-6429">
                  <Award className="w-6 h-6" data-id="element-6430" /> Beneficios Clave
                </h3>
                <ul className="space-y-4" data-id="element-6431">
                  {product.benefits.map((benefit, idx) => <li key={idx} className="flex items-start gap-3" data-id="element-6432">
                      <div className="w-6 h-6 rounded-full bg-primary-100 flex items-center justify-center shrink-0 mt-0.5 shadow-sm" data-id="element-6433">
                        <Check className="w-3.5 h-3.5 text-primary-700 stroke-[3]" data-id="element-6434" />
                      </div>
                      <span className="font-medium text-charcoal-700 text-lg" data-id="element-6435">
                        {benefit}
                      </span>
                    </li>)}
                </ul>
              </div>

              <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm" data-id="element-6436">
                <h3 className="font-serif font-bold text-2xl text-charcoal-900 mb-6" data-id="element-6437">
                  Especificaciones
                </h3>
                <div className="grid grid-cols-2 gap-6" data-id="element-6438">
                  {[{
                label: 'Proteína',
                value: product.nutritionalInfo?.crudeProtein || '28%',
                icon: Scale
              }, {
                label: 'Grasa',
                value: product.nutritionalInfo?.crudeFat || '15%',
                icon: Zap
              }, {
                label: 'Calorías',
                value: `${product.nutritionalInfo?.kcalPer100g || 380} kcal`,
                icon: Utensils
              }, {
                label: 'Vida Útil',
                value: '18 meses',
                icon: Calendar
              }].map((spec, idx) => <div key={idx} className="flex items-center gap-3 p-3 rounded-xl bg-gray-50/50" data-id="element-6439">
                      <div className="w-10 h-10 rounded-lg bg-white shadow-sm flex items-center justify-center text-gray-400" data-id="element-6440">
                        <spec.icon className="w-5 h-5" data-id="element-6441" />
                      </div>
                      <div data-id="element-6442">
                        <p className="text-[10px] uppercase font-bold text-gray-400 tracking-wider" data-id="element-6443">
                          {spec.label}
                        </p>
                        <p className="text-base font-bold text-charcoal-800" data-id="element-6444">
                          {spec.value}
                        </p>
                      </div>
                    </div>)}
                </div>
              </div>
            </div>

            {/* Ingredient Sourcing Section - Enhanced */}
            {enrichedData?.detailedIngredients && <div className="mb-12" data-id="element-6445">
                <h3 className="font-serif font-bold text-2xl text-charcoal-900 mb-6 flex items-center gap-2" data-id="element-6446">
                  <Leaf className="w-6 h-6 text-emerald-500" data-id="element-6447" /> Ingredientes con
                  Nombre y Apellido
                </h3>
                <div className="grid gap-4" data-id="element-6448">
                  {enrichedData.detailedIngredients.map((ing, idx) => <div key={idx} className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow flex flex-col md:flex-row gap-6 items-start md:items-center" data-id="element-6449">
                      <div className="flex-1" data-id="element-6450">
                        <div className="flex items-center gap-3 mb-2" data-id="element-6451">
                          <span className="font-bold text-lg text-charcoal-900" data-id="element-6452">
                            {ing.name}
                          </span>
                          <Badge variant="outline" className="text-emerald-600 border-emerald-200 bg-emerald-50" data-id="element-6453">
                            {ing.percentage}%
                          </Badge>
                        </div>
                        <p className="text-sm text-gray-500 flex items-center gap-1 mb-3" data-id="element-6454">
                          <MapPin className="w-3.5 h-3.5" data-id="element-6455" /> Origen:{' '}
                          <span className="font-medium text-gray-700" data-id="element-6456">
                            {ing.origin}
                          </span>
                        </p>
                        <div className="flex flex-wrap gap-2" data-id="element-6457">
                          {ing.benefits.map((b, i) => <span key={i} className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-md font-medium" data-id="element-6458">
                              {b}
                            </span>)}
                        </div>
                      </div>
                      <div className="w-full md:w-auto pl-0 md:pl-6 border-l-0 md:border-l border-gray-100" data-id="element-6459">
                        <p className="text-xs text-gray-400 uppercase tracking-wider mb-1" data-id="element-6460">
                          Procesado
                        </p>
                        <p className="text-sm font-medium text-charcoal-700" data-id="element-6461">
                          {ing.processing}
                        </p>
                      </div>
                    </div>)}
                </div>
              </div>}
          </div>
        </div>
  }, {
    id: 'nutrition',
    label: 'Nutrición Detallada',
    content: <div className="space-y-12 animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-6462">
          {/* Guaranteed Analysis Table */}
          {enrichedData?.nutritionalAnalysis && <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm" data-id="element-6463">
              <h3 className="font-serif font-bold text-2xl text-charcoal-900 mb-6" data-id="element-6464">
                Análisis Garantizado
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-6465">
                {Object.entries(enrichedData.nutritionalAnalysis.guaranteedAnalysis).map(([key, val]: [string, any]) => <div key={key} className="flex flex-col border-b border-gray-100 pb-4" data-id="element-6466">
                    <span className="text-sm text-gray-500 capitalize mb-1" data-id="element-6467">
                      {key.replace(/([A-Z])/g, ' $1').trim()}
                    </span>
                    <div className="flex items-baseline gap-1" data-id="element-6468">
                      <span className="text-2xl font-bold text-charcoal-900" data-id="element-6469">
                        {val.min ? `${val.min}` : val.max ? `<${val.max}` : val}
                      </span>
                      <span className="text-sm text-gray-400" data-id="element-6470">{val.unit}</span>
                    </div>
                    {val.min && val.max && <div className="w-full h-1.5 bg-gray-100 rounded-full mt-2 overflow-hidden" data-id="element-6471">
                        <div className="h-full bg-primary-500 rounded-full" style={{
                width: '60%'
              }} data-id="element-6472" />
                      </div>}
                  </div>)}
              </div>
            </div>}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12" data-id="element-6473">
            <div data-id="element-6474">
              <h3 className="font-serif font-bold text-2xl text-charcoal-900 mb-6" data-id="element-6475">
                Perfil de Macronutrientes
              </h3>
              <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm mb-6" data-id="element-6476">
                <BarChart data={[{
              label: 'Proteína',
              value: parseFloat(product.nutritionalInfo?.crudeProtein || '0'),
              color: '#10B981'
            }, {
              label: 'Grasa',
              value: parseFloat(product.nutritionalInfo?.crudeFat || '0'),
              color: '#F59E0B'
            }, {
              label: 'Carbos',
              value: 35,
              color: '#3B82F6'
            }, {
              label: 'Fibra',
              value: parseFloat(product.nutritionalInfo?.crudeFiber || '0'),
              color: '#8B5CF6'
            }]} height={250} data-id="element-6477" />
              </div>
            </div>

            <div data-id="element-6478">
              <h3 className="font-serif font-bold text-2xl text-charcoal-900 mb-6" data-id="element-6479">
                Comparativa Estándar
              </h3>
              <ComparisonTable headers={['Nutriente', 'Petiora', 'AAFCO Min', 'Competencia']} rows={[['Proteína', product.nutritionalInfo?.crudeProtein || '28%', '18%', '22%'], ['Grasa', product.nutritionalInfo?.crudeFat || '15%', '5%', '12%'], ['Calcio', '1.2%', '0.6%', '1.0%'], ['Omega-3', '0.8%', 'N/A', '0.3%']]} highlightColumn={1} data-id="element-6480" />
            </div>
          </div>
        </div>
  }, {
    id: 'process',
    label: 'Elaboración',
    content: <div className="space-y-12 animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-6481">
          <div className="text-center max-w-2xl mx-auto mb-12" data-id="element-6482">
            <h3 className="font-serif font-bold text-2xl text-charcoal-900 mb-4" data-id="element-6483">
              Del Campo a su Cuenco
            </h3>
            <p className="text-charcoal-600" data-id="element-6484">
              Utilizamos tecnología de cocción lenta (Sous-vide) para preservar
              hasta el 98% de los nutrientes originales, a diferencia de la
              extrusión industrial a altas temperaturas.
            </p>
          </div>

          <div className="bg-white p-8 rounded-[2rem] border border-gray-100 shadow-soft-xl" data-id="element-6485">
            <StepIndicator steps={[{
          title: 'Selección',
          description: 'Ingredientes locales frescos',
          status: 'completed'
        }, {
          title: 'Mezclado',
          description: 'En frío para no alterar',
          status: 'completed'
        }, {
          title: 'Cocción',
          description: 'Vapor lento 80°C',
          status: 'current'
        }, {
          title: 'Envasado',
          description: 'Atmósfera protectora',
          status: 'upcoming'
        }]} currentStep={2} data-id="element-6486" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-6487">
            <Card className="p-8 text-center hover:border-primary-200 transition-colors group" data-id="element-6488">
              <div className="w-16 h-16 rounded-2xl bg-emerald-50 text-emerald-600 flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform" data-id="element-6489">
                <Thermometer className="w-8 h-8" data-id="element-6490" />
              </div>
              <h4 className="font-bold text-xl text-charcoal-900 mb-3" data-id="element-6491">
                Cocción Lenta
              </h4>
              <p className="text-charcoal-500 leading-relaxed" data-id="element-6492">
                Cocinamos a baja temperatura para mantener intactas las
                proteínas y vitaminas termosensibles.
              </p>
            </Card>
            <Card className="p-8 text-center hover:border-primary-200 transition-colors group" data-id="element-6493">
              <div className="w-16 h-16 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform" data-id="element-6494">
                <FlaskConical className="w-8 h-8" data-id="element-6495" />
              </div>
              <h4 className="font-bold text-xl text-charcoal-900 mb-3" data-id="element-6496">
                Testado en Lab
              </h4>
              <p className="text-charcoal-500 leading-relaxed" data-id="element-6497">
                Protocolo de "Liberación Positiva": ningún producto sale sin
                análisis microbiológico negativo.
              </p>
            </Card>
            <Card className="p-8 text-center hover:border-primary-200 transition-colors group" data-id="element-6498">
              <div className="w-16 h-16 rounded-2xl bg-amber-50 text-amber-600 flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform" data-id="element-6499">
                <Package className="w-8 h-8" data-id="element-6500" />
              </div>
              <h4 className="font-bold text-xl text-charcoal-900 mb-3" data-id="element-6501">
                Frescura Garantizada
              </h4>
              <p className="text-charcoal-500 leading-relaxed" data-id="element-6502">
                Envases con barrera de oxígeno y nitrógeno que mantienen el
                sabor natural sin conservantes artificiales.
              </p>
            </Card>
          </div>
        </div>
  }, {
    id: 'qa',
    label: 'Calidad Certificada',
    content: <div className="space-y-10 animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-6503">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-6504">
            {[{
          name: 'AAFCO Compliant',
          desc: 'Cumple perfiles nutricionales americanos',
          icon: Check,
          color: 'text-emerald-600',
          bg: 'bg-emerald-50'
        }, {
          name: 'Grado Humano',
          desc: 'Ingredientes aptos para consumo humano',
          icon: Utensils,
          color: 'text-blue-600',
          bg: 'bg-blue-50'
        }, {
          name: 'Non-GMO',
          desc: 'Sin organismos genéticamente modificados',
          icon: Leaf,
          color: 'text-amber-600',
          bg: 'bg-amber-50'
        }, {
          name: 'Vet Formulated',
          desc: 'Desarrollado por nutricionistas veterinarios',
          icon: Award,
          color: 'text-purple-600',
          bg: 'bg-purple-50'
        }].map((cert, idx) => <div key={idx} className="flex items-start gap-4 p-6 rounded-2xl border border-gray-100 hover:border-gray-200 hover:shadow-md transition-all bg-white" data-id="element-6505">
                <div className={`w-12 h-12 rounded-xl ${cert.bg} flex items-center justify-center shrink-0`} data-id="element-6506">
                  <cert.icon className={`w-6 h-6 ${cert.color}`} data-id="element-6507" />
                </div>
                <div data-id="element-6508">
                  <h4 className="font-bold text-charcoal-900 text-lg mb-1" data-id="element-6509">
                    {cert.name}
                  </h4>
                  <p className="text-gray-500 text-sm" data-id="element-6510">{cert.desc}</p>
                </div>
              </div>)}
          </div>

          <div className="bg-charcoal-900 rounded-[2rem] overflow-hidden relative text-white p-8 md:p-12" data-id="element-6511">
            <div className="relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-8 items-center" data-id="element-6512">
              <div data-id="element-6513">
                <Badge variant="primary" className="mb-4" data-id="element-6514">
                  Transparencia Total
                </Badge>
                <h3 className="font-serif font-bold text-3xl mb-4" data-id="element-6515">
                  Conoce a nuestros veterinarios
                </h3>
                <p className="text-gray-300 mb-6 text-lg" data-id="element-6516">
                  Nuestro equipo de expertos formula cada receta basándose en la
                  última evidencia científica, no en tendencias de marketing.
                </p>
                <div className="flex items-center gap-4 bg-white/10 p-4 rounded-xl backdrop-blur-sm" data-id="element-6517">
                  <img src="https://i.pravatar.cc/150?u=vet" alt="Dr. Sarah" className="w-14 h-14 rounded-full border-2 border-primary-500" data-id="element-6518" />
                  <div data-id="element-6519">
                    <p className="font-bold text-lg" data-id="element-6520">Dra. Sarah Méndez</p>
                    <p className="text-sm text-gray-400" data-id="element-6521">
                      Nutricionista Veterinaria Jefe
                    </p>
                  </div>
                </div>
              </div>
              <div className="relative rounded-xl overflow-hidden shadow-2xl border border-white/10 aspect-video bg-black/50 flex items-center justify-center group cursor-pointer" data-id="element-6522">
                <PlayCircle className="w-16 h-16 text-white opacity-80 group-hover:opacity-100 transition-opacity scale-100 group-hover:scale-110 duration-300" data-id="element-6523" />
                <span className="absolute bottom-4 text-sm font-medium opacity-80" data-id="element-6524">
                  Ver proceso de formulación
                </span>
              </div>
            </div>
          </div>
        </div>
  }, {
    id: 'reviews',
    label: `Reseñas (${product.reviewCount})`,
    content: <ProductReviews productId={product.id} data-id="element-6525" />
  }, {
    id: 'faq',
    label: 'Preguntas Frecuentes',
    content: <div className="max-w-3xl mx-auto animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-6526">
          <div className="space-y-4" data-id="element-6527">
            {(faqs || [{
          question: '¿Es adecuado para perros con alergias?',
          answer: 'Sí, nuestra fórmula es hipoalergénica y libre de trigo, soja y maíz.'
        }, {
          question: '¿Cómo hago la transición?',
          answer: 'Recomendamos mezclar gradualmente con su comida actual durante 7-10 días.'
        }, {
          question: '¿Es apto para cachorros?',
          answer: 'Esta fórmula está optimizada para adultos. Para cachorros, recomendamos nuestra línea "Puppy Growth".'
        }]).map((item, idx) => <div key={idx} className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm hover:shadow-md transition-shadow" data-id="element-6528">
                <h4 className="font-bold text-charcoal-900 flex items-start gap-3 mb-3 text-lg" data-id="element-6529">
                  <HelpCircle className="w-6 h-6 text-primary-500 shrink-0 mt-0.5" data-id="element-6530" />
                  {item.question}
                </h4>
                <p className="text-charcoal-600 pl-9 text-base leading-relaxed" data-id="element-6531">
                  {item.answer}
                </p>
              </div>)}
          </div>
          <div className="mt-10 text-center bg-gray-50 p-8 rounded-2xl" data-id="element-6532">
            <p className="text-charcoal-600 mb-4 font-medium" data-id="element-6533">
              ¿No encuentras tu respuesta?
            </p>
            <Button variant="outline" className="bg-white" data-id="element-6534">
              Contactar con un experto en nutrición
            </Button>
          </div>
        </div>
  }];
  return <PageTransition data-id="element-6535">
      <div className="bg-[#FAF9F6] min-h-screen pb-20 font-sans" data-id="element-6536">
        {/* Breadcrumb Navigation */}
        <div className="bg-white border-b border-gray-100 sticky top-[72px] sm:top-[116px] z-30 shadow-sm" data-id="element-6537">
          <div className="container-custom py-3" data-id="element-6538">
            <Breadcrumb items={breadcrumbItems} data-id="element-6539" />
          </div>
        </div>

        <div className="container-custom pt-8 lg:pt-12" data-id="element-6540">
          {/* Main Product Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 mb-20" data-id="element-6541">
            {/* Left Column: Gallery & Trust (5 cols) */}
            <div className="lg:col-span-7 space-y-8" data-id="element-6542">
              <div className="sticky top-32" data-id="element-6543">
                <ImageZoom images={galleryImages} alt={product.name} data-id="element-6544" />

                {/* Trust Badges Row */}
                <div className="grid grid-cols-3 gap-4 mt-8" data-id="element-6545">
                  <div className="flex flex-col items-center text-center gap-2 p-4 bg-white rounded-2xl border border-gray-100 shadow-sm" data-id="element-6546">
                    <div className="w-10 h-10 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center" data-id="element-6547">
                      <Leaf className="w-5 h-5" data-id="element-6548" />
                    </div>
                    <span className="text-xs font-bold text-charcoal-600" data-id="element-6549">
                      100% Natural
                    </span>
                  </div>
                  <div className="flex flex-col items-center text-center gap-2 p-4 bg-white rounded-2xl border border-gray-100 shadow-sm" data-id="element-6550">
                    <div className="w-10 h-10 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center" data-id="element-6551">
                      <ShieldCheck className="w-5 h-5" data-id="element-6552" />
                    </div>
                    <span className="text-xs font-bold text-charcoal-600" data-id="element-6553">
                      Garantía 30 días
                    </span>
                  </div>
                  <div className="flex flex-col items-center text-center gap-2 p-4 bg-white rounded-2xl border border-gray-100 shadow-sm" data-id="element-6554">
                    <div className="w-10 h-10 rounded-full bg-amber-50 text-amber-600 flex items-center justify-center" data-id="element-6555">
                      <Truck className="w-5 h-5" data-id="element-6556" />
                    </div>
                    <span className="text-xs font-bold text-charcoal-600" data-id="element-6557">
                      Envío Rápido
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column: Info & Actions (7 cols) */}
            <div className="lg:col-span-5 flex flex-col" data-id="element-6558">
              {/* Header Info */}
              <div className="mb-6" data-id="element-6559">
                <div className="flex items-center justify-between mb-3" data-id="element-6560">
                  <div className="flex items-center gap-2 bg-white px-3 py-1 rounded-full border border-gray-100 shadow-sm" data-id="element-6561">
                    <div className="flex text-amber-400" data-id="element-6562">
                      {[...Array(5)].map((_, i) => <Star key={i} className={`w-3.5 h-3.5 ${i < Math.floor(product.rating || 0) ? 'fill-current' : 'text-gray-200 fill-gray-200'}`} data-id="element-6563" />)}
                    </div>
                    <span className="text-xs font-bold text-charcoal-600 border-l border-gray-200 pl-2 ml-1" data-id="element-6564">
                      {product.reviewCount} reseñas
                    </span>
                  </div>
                  {product.inStock ? <Badge variant="success" size="sm" className="font-bold" data-id="element-6565">
                      En Stock
                    </Badge> : <Badge variant="error" size="sm" className="font-bold" data-id="element-6566">
                      Agotado
                    </Badge>}
                </div>

                <h1 className="text-3xl md:text-4xl lg:text-5xl font-serif font-bold text-charcoal-900 mb-4 leading-tight" data-id="element-6567">
                  {product.name}
                </h1>

                <p className="text-charcoal-500 text-lg font-light leading-relaxed mb-6" data-id="element-6568">
                  {product.description.substring(0, 150)}...
                </p>
              </div>

              {/* Pricing Card */}
              <div className="bg-white p-6 rounded-[2rem] border border-gray-100 shadow-soft-xl mb-8 relative overflow-hidden" data-id="element-6569">
                <div className="absolute top-0 right-0 w-32 h-32 bg-primary-50 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" data-id="element-6570" />

                <div className="relative z-10" data-id="element-6571">
                  <div className="flex items-baseline gap-3 mb-6" data-id="element-6572">
                    <span className="text-4xl font-bold text-charcoal-900 font-serif" data-id="element-6573">
                      {subscriptionMode === 'subscribe' ? (product.price * 0.9).toFixed(2) : product.price.toFixed(2)}
                      €
                    </span>
                    {product.originalPrice && <span className="text-lg text-gray-400 line-through" data-id="element-6574">
                        {product.originalPrice.toFixed(2)}€
                      </span>}
                  </div>

                  {/* Subscription Toggle */}
                  <div className="space-y-3 mb-8" data-id="element-6575">
                    <label className={`flex items-center justify-between p-4 rounded-xl border-2 cursor-pointer transition-all ${subscriptionMode === 'onetime' ? 'border-primary-600 bg-primary-50/30' : 'border-gray-100 hover:border-gray-200'}`} onClick={() => setSubscriptionMode('onetime')} data-id="element-6576">
                      <div className="flex items-center gap-3" data-id="element-6577">
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${subscriptionMode === 'onetime' ? 'border-primary-600' : 'border-gray-300'}`} data-id="element-6578">
                          {subscriptionMode === 'onetime' && <div className="w-2.5 h-2.5 rounded-full bg-primary-600" data-id="element-6579" />}
                        </div>
                        <span className="font-bold text-charcoal-800" data-id="element-6580">
                          Compra Única
                        </span>
                      </div>
                    </label>

                    <label className={`flex items-center justify-between p-4 rounded-xl border-2 cursor-pointer transition-all ${subscriptionMode === 'subscribe' ? 'border-primary-600 bg-primary-50/30' : 'border-gray-100 hover:border-gray-200'}`} onClick={() => setSubscriptionMode('subscribe')} data-id="element-6581">
                      <div className="flex items-center gap-3" data-id="element-6582">
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${subscriptionMode === 'subscribe' ? 'border-primary-600' : 'border-gray-300'}`} data-id="element-6583">
                          {subscriptionMode === 'subscribe' && <div className="w-2.5 h-2.5 rounded-full bg-primary-600" data-id="element-6584" />}
                        </div>
                        <div data-id="element-6585">
                          <span className="font-bold text-charcoal-800 block" data-id="element-6586">
                            Suscríbete y Ahorra
                          </span>
                          <span className="text-xs text-primary-700 font-bold" data-id="element-6587">
                            Ahorras 10% en cada pedido
                          </span>
                        </div>
                      </div>
                      <Badge variant="primary" size="sm" data-id="element-6588">
                        Popular
                      </Badge>
                    </label>

                    <AnimatePresence data-id="element-6589">
                      {subscriptionMode === 'subscribe' && <motion.div initial={{
                      height: 0,
                      opacity: 0
                    }} animate={{
                      height: 'auto',
                      opacity: 1
                    }} exit={{
                      height: 0,
                      opacity: 0
                    }} className="overflow-hidden pl-8" data-id="element-6590">
                          <div className="flex items-center gap-3 pt-2" data-id="element-6591">
                            <span className="text-sm font-medium text-charcoal-600" data-id="element-6592">
                              Entrega cada:
                            </span>
                            <select value={deliveryFreq} onChange={e => setDeliveryFreq(e.target.value)} className="bg-gray-50 border border-gray-200 rounded-lg text-sm py-1.5 px-3 focus:ring-2 focus:ring-primary-500 outline-none" data-id="element-6593">
                              <option value="2" data-id="element-6594">2 Semanas</option>
                              <option value="4" data-id="element-6595">4 Semanas</option>
                              <option value="6" data-id="element-6596">6 Semanas</option>
                              <option value="8" data-id="element-6597">8 Semanas</option>
                            </select>
                          </div>
                        </motion.div>}
                    </AnimatePresence>
                  </div>

                  {/* Weight Selector */}
                  <div className="mb-8" data-id="element-6598">
                    <div className="flex justify-between items-center mb-3" data-id="element-6599">
                      <span className="text-sm font-bold text-charcoal-700" data-id="element-6600">
                        Peso:
                      </span>
                      <span className="text-xs text-primary-600 font-bold cursor-pointer hover:underline" data-id="element-6601">
                        ¿Cuál elegir?
                      </span>
                    </div>
                    <div className="flex gap-3" data-id="element-6602">
                      {['2kg', '5kg', '10kg'].map(w => <button key={w} onClick={() => setSelectedWeight(w)} className={`flex-1 py-2.5 rounded-xl text-sm font-bold border-2 transition-all ${selectedWeight === w ? 'border-charcoal-900 bg-charcoal-900 text-white' : 'border-gray-100 text-charcoal-600 hover:border-gray-300'}`} data-id="element-6603">
                          {w}
                        </button>)}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-3" ref={ctaRef} data-id="element-6604">
                    <div className="flex items-center bg-gray-50 rounded-xl border border-gray-200 h-14 w-32 shrink-0" data-id="element-6605">
                      <button onClick={() => setQuantity(Math.max(1, quantity - 1))} className="w-10 h-full flex items-center justify-center text-gray-500 hover:text-charcoal-900 font-bold text-lg" data-id="element-6606">
                        -
                      </button>
                      <span className="flex-1 text-center font-bold text-charcoal-900" data-id="element-6607">
                        {quantity}
                      </span>
                      <button onClick={() => setQuantity(quantity + 1)} className="w-10 h-full flex items-center justify-center text-gray-500 hover:text-charcoal-900 font-bold text-lg" data-id="element-6608">
                        +
                      </button>
                    </div>
                    <Button size="xl" className="flex-1 h-14 rounded-xl font-bold text-lg shadow-lg shadow-primary-900/20" onClick={() => addToCart(product, quantity)} pulse={product.inStock} data-id="element-6609">
                      Añadir <ShoppingCart className="ml-2 w-5 h-5" data-id="element-6610" />
                    </Button>
                    <button onClick={() => isWishlisted ? removeFromWishlist(product.id) : addToWishlist(product)} className={`w-14 h-14 rounded-xl border-2 flex items-center justify-center transition-all shrink-0 ${isWishlisted ? 'border-rose-200 bg-rose-50 text-rose-500' : 'border-gray-200 text-gray-400 hover:border-rose-200 hover:text-rose-500'}`} data-id="element-6611">
                      <Heart className={`w-6 h-6 ${isWishlisted ? 'fill-current' : ''}`} data-id="element-6612" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Delivery Estimator */}
              <div className="bg-gray-50 p-4 rounded-xl border border-gray-100 flex items-start gap-3 mb-8" data-id="element-6613">
                <MapPin className="w-5 h-5 text-charcoal-400 mt-0.5" data-id="element-6614" />
                <div data-id="element-6615">
                  <p className="text-sm font-bold text-charcoal-800" data-id="element-6616">
                    Entrega estimada
                  </p>
                  <p className="text-xs text-charcoal-500" data-id="element-6617">
                    Llega el{' '}
                    <span className="font-bold text-charcoal-700" data-id="element-6618">
                      Miércoles, 24 Oct
                    </span>{' '}
                    a{' '}
                    <span className="underline cursor-pointer" data-id="element-6619">
                      Madrid (28001)
                    </span>
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Detailed Content Tabs */}
          <div className="mb-24" data-id="element-6620">
            <Tabs tabs={tabs} defaultTab="description" data-id="element-6621" />
          </div>

          {/* Feeding Calculator Section */}
          <div className="mb-24" data-id="element-6622">
            <div className="bg-charcoal-900 rounded-[3rem] p-8 md:p-16 text-white relative overflow-hidden" data-id="element-6623">
              <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-primary-900/30 rounded-full blur-[100px] -translate-y-1/2 translate-x-1/3" data-id="element-6624" />
              <div className="relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-6625">
                <div data-id="element-6626">
                  <Badge variant="primary" className="mb-6" data-id="element-6627">
                    Herramienta Interactiva
                  </Badge>
                  <h2 className="text-4xl md:text-5xl font-serif font-bold mb-6 leading-tight" data-id="element-6628">
                    Calculadora de Raciones
                  </h2>
                  <p className="text-gray-300 text-lg mb-10 leading-relaxed" data-id="element-6629">
                    Cada mascota es única. Calcula la ración diaria exacta
                    basándote en su peso, edad y nivel de actividad para
                    asegurar una salud óptima.
                  </p>
                  <FeedingCalculator data-id="element-6630" />
                </div>
                <div className="hidden lg:block relative" data-id="element-6631">
                  <div className="absolute inset-0 bg-gradient-to-tr from-charcoal-900/50 to-transparent z-10 rounded-3xl" data-id="element-6632" />
                  <img src="https://images.unsplash.com/photo-1583337130417-3346a1be7dee?auto=format&fit=crop&q=80&w=800" alt="Perro comiendo" className="rounded-3xl shadow-2xl transform rotate-3 hover:rotate-0 transition-transform duration-700" data-id="element-6633" />
                </div>
              </div>
            </div>
          </div>

          {/* Related Products */}
          <section className="mb-24" data-id="element-6634">
            <div className="flex items-center justify-between mb-10" data-id="element-6635">
              <div data-id="element-6636">
                <h2 className="text-3xl font-bold font-serif text-charcoal-900 mb-2" data-id="element-6637">
                  Completa su menú
                </h2>
                <p className="text-charcoal-500" data-id="element-6638">
                  Productos que combinan perfectamente
                </p>
              </div>
              <Link to="/productos" data-id="element-6639">
                <Button variant="outline" data-id="element-6640">Ver todo</Button>
              </Link>
            </div>
            <RelatedProducts currentProductId={product.id} category={product.category} data-id="element-6641" />
          </section>
        </div>

        {/* Sticky Mobile Bar */}
        <AnimatePresence data-id="element-6642">
          {showStickyBar && <motion.div initial={{
          y: 100
        }} animate={{
          y: 0
        }} exit={{
          y: 100
        }} className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 shadow-2xl z-40 lg:hidden safe-area-bottom" data-id="element-6643">
              <div className="flex items-center gap-4" data-id="element-6644">
                <div className="flex-1" data-id="element-6645">
                  <p className="text-xs text-charcoal-500 font-bold uppercase" data-id="element-6646">
                    Total
                  </p>
                  <p className="text-xl font-bold text-charcoal-900 font-serif" data-id="element-6647">
                    {(product.price * quantity).toFixed(2)}€
                  </p>
                </div>
                <Button size="lg" className="flex-1 shadow-lg" onClick={() => addToCart(product, quantity)} data-id="element-6648">
                  Añadir
                </Button>
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>
    </PageTransition>;
}